import { useState, useEffect, useRef, KeyboardEvent, ClipboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { apiStore } from '@/stores/ApiStore';
import { authFlowStore } from '@/stores/AuthFlowStore';
import { sendHandoff, sendRestoreFailed, sendRestoreSuccess } from '@/hooks/useWebViewMessaging';
import { AccountLayout } from '@/components/ui/AccountLayout';
import { PaywallHeader } from '@/components/paywall/PaywallHeader';
import { CtaButton } from '@/components/ui/CtaButton';
import { ROUTES } from '@/constants/routes';
import type { SubscriptionProcessResponse, SubscriptionStatusResponse } from '@/types/api';

const DIGITS = 6;

const Header = styled.section`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1;
`;

const Hint = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 10px;
  margin-top: 16px;
`;

const Otp = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const OtpInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 64px;
  text-align: center;
  font-size: 20px;
  border: 1.5px solid ${({ $error }) => ($error ? 'var(--error-color)' : '#e5e7eb')};
  border-radius: 12px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: ${({ $error }) => ($error ? 'var(--error-color)' : '#000000')};
  }
`;

const ErrorMessage = styled.p`
  margin-top: 4px;
  color: var(--error-color);
  font-size: 16px;
  line-height: 125%;
  text-align: center;
`;

const CtaSection = styled.div`
  margin-top: auto;
  margin-bottom: 24px;
`;

const isSubscriptionProcessResponse = (
  response: SubscriptionStatusResponse,
): response is SubscriptionProcessResponse => typeof response.status === 'object';

export const LoginCodePage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [digits, setDigits] = useState<string[]>(Array(DIGITS).fill(''));
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    Array.from({ length: DIGITS }, () => null),
  );

  const isRelogin = searchParams.get('relogin') === 'true';
  const isRestore = searchParams.get('restore') === 'true';

  useEffect(() => {
    if (!authFlowStore.email) {
      navigate(ROUTES.AUTH.LOGIN);
      return;
    }
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, [navigate]);

  const isComplete = digits.every((d) => d.length === 1);

  const onInput = (value: string, idx: number) => {
    const cleanValue = value.replace(/\D/g, '');
    setError(false);

    if (cleanValue.length > 1) {
      // Handle paste of multiple digits
      const nums = cleanValue.slice(0, DIGITS - idx).split('');
      const newDigits = [...digits];
      for (let i = 0; i < nums.length && idx + i < DIGITS; i++) {
        newDigits[idx + i] = nums[i] || '';
      }
      setDigits(newDigits);
      setTimeout(() => {
        const focusIndex = Math.min(idx + nums.length, DIGITS - 1);
        inputRefs.current[focusIndex]?.focus();
      }, 0);
    }
    else {
      const v = cleanValue.slice(0, 1);
      const newDigits = [...digits];
      newDigits[idx] = v;
      setDigits(newDigits);
      if (v && idx < DIGITS - 1) {
        setTimeout(() => inputRefs.current[idx + 1]?.focus(), 0);
      }
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>, idx: number) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key)) {
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (digits[idx]) {
          const newDigits = [...digits];
          newDigits[idx] = '';
          setDigits(newDigits);
          return;
        }
        if (idx > 0) {
          setTimeout(() => inputRefs.current[idx - 1]?.focus(), 0);
        }
      }
      return;
    }

    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
      return;
    }

    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text') || '';
    const nums = text.replace(/\D/g, '').slice(0, DIGITS).split('');
    if (!nums.length) return;

    setError(false);

    const newDigits = Array(DIGITS).fill('');
    for (let i = 0; i < DIGITS; i++) {
      newDigits[i] = nums[i] || '';
    }
    setDigits(newDigits);

    setTimeout(() => {
      if (nums.length >= DIGITS) {
        inputRefs.current[DIGITS - 1]?.focus();
      }
      else {
        inputRefs.current[nums.length]?.focus();
      }
    }, 0);
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete || isLoading) return;

    setIsLoading(true);
    setError(false);

    try {
      const code = digits.join('');
      const email = authFlowStore.email;
      if (!email) return;

      await apiStore.verifyCode(email, code);

      // Get meta parameter to preserve it through the flow
      const metaParam = searchParams.get('meta');
      const params = new URLSearchParams();
      if (metaParam) {
        params.set('meta', metaParam);
      }
      if (isRestore) {
        params.set('restore', 'true');
      }
      const paywallUrl = params.toString()
        ? `${ROUTES.PAYWALL.V1}?${params.toString()}`
        : ROUTES.PAYWALL.V1;

      // If restore parameter is passed, check subscription status
      if (isRestore) {
        try {
          const response = await apiStore.getSubscriptionStatus();

          const subscriptionStatus = response && isSubscriptionProcessResponse(response)
            ? response.status
            : response;

          // The backend is authoritative for entitlement, including canceled subscriptions
          // that remain available until accessUntil.
          if (subscriptionStatus?.hasAccess) {
            sendRestoreSuccess();
            const handoffResult = await apiStore.createHandoff();
            if (handoffResult.ok && handoffResult.token) {
              sendHandoff(handoffResult.token);
            }
            // Redirect to paywall with meta
            navigate(paywallUrl);
          }
          else {
            // No subscription found (204 or null) - send failed message and redirect
            sendRestoreFailed();
            navigate(paywallUrl);
          }
        }
        catch (error) {
          // Request failed or any other error - send failed message and redirect
          console.error('Failed to check subscription status during restore:', error);
          sendRestoreFailed();
          navigate(paywallUrl);
        }
      }
      // If relogin parameter is passed, create handoff token and send to mobile app
      else if (isRelogin) {
        const handoffResult = await apiStore.createHandoff();
        if (handoffResult.ok && handoffResult.token) {
          sendHandoff(handoffResult.token);
        }
      }
      else {
        // Without relogin parameter, just redirect to subscription page
        navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
      }
    }
    catch {
      setError(true);
    }
    finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    navigate(ROUTES.AUTH.LOGIN);
  };

  return (
    <AccountLayout showLogo showBack onBack={goBack}>
      <Header>
        <PaywallHeader>Enter code</PaywallHeader>
      </Header>
      <Form onSubmit={verify}>
        <Hint>We've sent an activation code to your email</Hint>
        <Otp>
          {digits.map((d, idx) => (
            <OtpInput
              key={idx}
              ref={(el) => (inputRefs.current[idx] = el)}
              $error={error}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              aria-label={`Digit ${idx + 1}`}
              value={d}
              onChange={(e) => onInput(e.target.value, idx)}
              onKeyDown={(e) => onKeyDown(e, idx)}
              onPaste={onPaste}
            />
          ))}
        </Otp>
        {error && <ErrorMessage>Wrong code, please try again</ErrorMessage>}
        {isComplete && (
          <CtaSection>
            <CtaButton label="Verify" loading={isLoading} disabled={!isComplete} type="submit" />
          </CtaSection>
        )}
      </Form>
    </AccountLayout>
  );
});
